import { Link, useLocation } from 'react-router-dom';
import { SchemaBreadcrumb } from '@/components/feature/SchemaOrg';

interface GuiaBreadcrumbProps {
  currentTitle: string;
}

export function GuiaBreadcrumb({ currentTitle }: GuiaBreadcrumbProps) {
  const location = useLocation();
  const breadcrumbItems = [
    { name: 'Inicio', url: 'https://toldos-malaga.es/' },
    { name: 'Guías', url: 'https://toldos-malaga.es/guias/' },
    { name: currentTitle, url: `https://toldos-malaga.es${location.pathname}` },
  ];

  return (
    <>
      <SchemaBreadcrumb items={breadcrumbItems} />
      <nav aria-label="Breadcrumb" className="w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-28 pb-2">
        <ol className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
          <li>
            <Link to="/" className="hover:text-primary-500 transition-colors duration-200">
              Inicio
            </Link>
          </li>
          <li>
            <i className="ri-arrow-right-s-line text-xs"></i>
          </li>
          <li>
            <Link to="/guias" className="hover:text-primary-500 transition-colors duration-200">
              Guías
            </Link>
          </li>
          <li>
            <i className="ri-arrow-right-s-line text-xs"></i>
          </li>
          <li className="text-foreground-700 font-medium truncate max-w-[200px] md:max-w-xs">
            {currentTitle}
          </li>
        </ol>
      </nav>
    </>
  );
}

interface ConversionBlockProps {
  title?: string;
  subtitle?: string;
  phoneDisplay?: string;
  phoneLink?: string;
  whatsappDisplay?: string;
  whatsappLink?: string;
}

export function ConversionBlock({
  title = '¿Estás en Málaga? Lo reparamos nosotros',
  subtitle = 'Nuestros tolderos profesionales se desplazan a cualquier punto de Málaga y provincia. Diagnóstico en menos de 24 horas y presupuesto sin compromiso.',
  phoneDisplay = '951 12 07 70',
  phoneLink = '+34951120770',
  whatsappDisplay = '627 21 33 47',
  whatsappLink = '34627213347',
}: ConversionBlockProps) {
  return (
    <div className="bg-[#0F5066] rounded-2xl p-5 md:p-6 lg:p-8 text-white">
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-accent-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-tools-line text-xl text-accent-400"></i>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg md:text-xl lg:text-2xl font-heading font-bold text-white leading-snug">
              {title}
            </h3>
          </div>
        </div>
        <p className="text-white/75 text-sm md:text-base leading-relaxed">
          {subtitle}
        </p>
        <div className="flex flex-col gap-3">
          <a
            href={`tel:${phoneLink}`}
            className="flex items-center justify-center gap-2 px-5 py-3 bg-white text-[#0F5066] rounded-full font-bold text-sm hover:bg-accent-100 transition-all duration-200 whitespace-nowrap shadow-lg shadow-black/10"
          >
            <i className="ri-phone-line text-xl"></i>
            <span>Llamar {phoneDisplay}</span>
          </a>
          <a
            href={`https://wa.me/${whatsappLink}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 px-5 py-3 bg-accent-500 text-foreground-900 rounded-full font-bold text-sm hover:bg-accent-400 transition-all duration-200 whitespace-nowrap shadow-lg shadow-black/10"
          >
            <i className="ri-whatsapp-line text-xl"></i>
            <span>WhatsApp {whatsappDisplay}</span>
          </a>
        </div>
      </div>
    </div>
  );
}

interface PriceTagProps {
  price: string;
  description?: string;
}

export function PriceTag({ price, description }: PriceTagProps) {
  return (
    <div className="inline-flex items-center gap-3 bg-secondary-100/80 border border-secondary-300/60 rounded-xl px-5 py-3">
      <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0">
        <i className="ri-price-tag-3-line text-white text-lg"></i>
      </div>
      <div>
        <span className="text-xs text-foreground-500 font-medium block leading-tight">
          {description || 'Precio orientativo'}
        </span>
        <span className="text-lg md:text-xl font-heading font-bold text-primary-600">
          {price}
        </span>
      </div>
    </div>
  );
}

interface FAQItem {
  question: string;
  answer: string;
}

interface GuiaFAQProps {
  faqs: FAQItem[];
}

export function SchemaFAQPage({ faqs }: GuiaFAQProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function GuiaFAQ({ faqs }: GuiaFAQProps) {
  return (
    <>
      <SchemaFAQPage faqs={faqs} />
      <div className="space-y-3">
        {faqs.map((faq, i) => (
          <details
            key={i}
            className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden transition-all duration-300"
          >
            <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
              <span className="text-sm md:text-base font-semibold text-foreground-900 pr-4">
                {faq.question}
              </span>
              <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 group-open:bg-primary-500 transition-colors duration-200">
                <i className="ri-arrow-down-s-line text-primary-600 group-open:text-white text-sm transition-transform duration-300 group-open:rotate-180"></i>
              </div>
            </summary>
            <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
              {faq.answer}
            </div>
          </details>
        ))}
      </div>
    </>
  );
}

export function GuiaRelacionadas() {
  const relatedGuides = [
    {
      to: '/guias/toldo-no-recoge',
      icon: 'ri-arrow-go-back-line',
      title: 'Toldo no recoge',
      desc: 'Causas y solución cuando el toldo no se enrolla correctamente.',
    },
    {
      to: '/guias/brazo-toldo-roto',
      icon: 'ri-tools-line',
      title: 'Brazo de toldo roto',
      desc: 'Reparación de brazos de punto recto y articulados.',
    },
    {
      to: '/guias/cambiar-lona-toldo',
      icon: 'ri-paint-brush-line',
      title: 'Cambiar lona o tela',
      desc: 'Cuándo y cómo sustituir la lona de tu toldo.',
    },
    {
      to: '/guias/toldo-descolgado',
      icon: 'ri-contrast-drop-line',
      title: 'Toldo descolgado',
      desc: 'Cómo arreglar un toldo que cuelga o está descosido.',
    },
    {
      to: '/guias/cambiar-motor-toldo',
      icon: 'ri-plug-line',
      title: 'Cambiar motor',
      desc: 'Guía para sustituir el motor de un toldo eléctrico.',
    },
  ];

  return (
    <div>
      <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">
        Otras guías que te pueden interesar
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {relatedGuides.map((guide) => (
          <Link
            key={guide.to}
            to={guide.to}
            className="flex items-start gap-3 p-4 rounded-xl bg-background-100/70 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-200 group"
          >
            <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 transition-colors duration-200">
              <i className={`${guide.icon} text-primary-600 text-base`}></i>
            </div>
            <div>
              <span className="text-sm font-semibold text-foreground-800 block group-hover:text-primary-600 transition-colors duration-200">
                {guide.title}
              </span>
              <span className="text-xs text-foreground-500 leading-snug">
                {guide.desc}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function SchemaArticle({
  headline,
  description,
  datePublished,
  dateModified,
  imageUrl,
  canonicalUrl,
}: {
  headline: string;
  description: string;
  datePublished: string;
  dateModified: string;
  imageUrl: string;
  canonicalUrl?: string;
}) {
  const ogImageUrl = 'https://toldos-malaga.es/og-image.jpg';
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline,
    description,
    datePublished,
    dateModified,
    image: imageUrl,
    author: {
      '@type': 'Organization',
      name: 'Toldos Málaga',
      url: 'https://toldos-malaga.es/',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Toldos Málaga',
      url: 'https://toldos-malaga.es/',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <meta property="og:title" content={`${headline} | Toldos Málaga`} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={canonicalUrl || 'https://toldos-malaga.es/guias'} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Toldos Málaga" />
      <meta property="og:locale" content="es_ES" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${headline} | Toldos Málaga`} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
    </>
  );
}